import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Type,
    MousePointer,
    Image,
    Square,
    QrCode,
    Grid3X3,
    Play,
    Users,
    Monitor,
    Palette,
    Camera,
    CreditCard,
    X,
    Hash,
} from 'lucide-react';
import {
    startDragFromPalette,
    endDragFromPalette,
} from './utils/state/canvasSlice';

interface ElementPaletteProps {
    isOpen: boolean;
    onClose: () => void;
    rightOffset?: number;
    embedded?: boolean; // New prop for embedded mode
}

const ELEMENT_TYPES = [
    {
        type: 'text' as UIElementType,
        name: 'Text',
        icon: Type,
        color: '#1F2937',
        description: 'Display static text',
    },
    {
        type: 'button' as UIElementType,
        name: 'Button',
        icon: MousePointer,
        color: '#3B82F6',
        description: 'Interactive button with actions',
    },
    {
        type: 'image' as UIElementType,
        name: 'Image',
        icon: Image,
        color: '#10B981',
        description: 'Display images or graphics',
    },
    {
        type: 'input' as UIElementType,
        name: 'Input',
        icon: Square,
        color: '#F59E0B',
        description: 'User input field',
    },
    {
        type: 'currentSessionQR' as UIElementType,
        name: 'Session QR',
        icon: QrCode,
        color: '#8B5CF6',
        description: 'QR code for current session',
    },
    {
        type: 'galleryGrid' as UIElementType,
        name: 'Gallery Grid',
        icon: Grid3X3,
        color: '#EF4444',
        description: 'Photo gallery grid',
    },
    {
        type: 'liveView' as UIElementType,
        name: 'Live View',
        icon: Play,
        color: '#06B6D4',
        description: 'Camera live preview',
    },
    {
        type: 'group' as UIElementType,
        name: 'Group',
        icon: Users,
        color: '#84CC16',
        description: 'Group multiple elements',
    },
    {
        type: 'dynamicText' as UIElementType,
        name: 'Dynamic Text',
        icon: Type,
        color: '#8B5CF6',
        description: 'Text that updates dynamically',
    },
    {
        type: 'paymentQR' as UIElementType,
        name: 'Payment QR',
        icon: CreditCard,
        color: '#059669',
        description: 'QR code for payments',
    },
    {
        type: 'outputPreview' as UIElementType,
        name: 'Output Preview',
        icon: Monitor,
        color: '#DC2626',
        description: 'Preview of processed output',
    },
    {
        type: 'mediaGrid' as UIElementType,
        name: 'Media Grid',
        icon: Grid3X3,
        color: '#7C3AED',
        description: 'Grid of media items',
    },
    {
        type: 'sessionGrid' as UIElementType,
        name: 'Session Grid',
        icon: Grid3X3,
        color: '#0891B2',
        description: 'Grid of session photos',
    },
    {
        type: 'selectedQR' as UIElementType,
        name: 'Selected QR',
        icon: QrCode,
        color: '#BE185D',
        description: 'QR code for selected items',
    },
    {
        type: 'artworkSelector' as UIElementType,
        name: 'Artwork Selector',
        icon: Palette,
        color: '#EA580C',
        description: 'Select from artwork options',
    },
    {
        type: 'codeScanner' as UIElementType,
        name: 'Code Scanner',
        icon: Camera,
        color: '#15803D',
        description: 'Scan QR codes or barcodes',
    },
    {
        type: 'phoneKeypad' as UIElementType,
        name: 'Phone Keypad',
        icon: Hash,
        color: '#7C2D12',
        description: 'Numeric keypad for phone input',
    },
];

export default function ElementPalette({
    isOpen,
    onClose,
    rightOffset = 340,
    embedded = false,
}: ElementPaletteProps) {
    const dispatch = useDispatch();
    const { isDragFromPalette, draggedElementType } = useSelector(
        (state: any) => state.canvas,
    );

    console.log(
        'ElementPalette render - isOpen:',
        isOpen,
        'rightOffset:',
        rightOffset,
    );

    if (!isOpen && !embedded) return null;

    // Removed click-to-select functionality to focus on drag-and-drop performance

    const handleDragStart = (
        e: React.DragEvent,
        elementType: UIElementType,
    ) => {
        console.log('Starting drag for:', elementType);
        e.dataTransfer.setData('application/element-type', elementType);
        e.dataTransfer.effectAllowed = 'copy';
        dispatch(startDragFromPalette(elementType));
    };

    const handleDragEnd = () => {
        console.log('Drag ended');
        dispatch(endDragFromPalette());
    };

    // const handleDragLeave = () => {
    //     // Clear hover state when leaving the drag area
    //     console.log('Drag left element palette');
    // };

    const handleCancelDrag = () => {
        dispatch(endDragFromPalette());
    };

    // Embedded mode renders differently
    if (embedded) {
        return (
            <div className="h-full flex flex-col">
                {/* Embedded header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    {isDragFromPalette && (
                        <p className="text-xs text-blue-600">
                            Dragging:{' '}
                            {
                                ELEMENT_TYPES.find(
                                    (e) => e.type === draggedElementType,
                                )?.name
                            }
                            <br />
                            Drop on a screen grid position
                        </p>
                    )}
                    {!isDragFromPalette && (
                        <p className="text-xs text-gray-500">
                            Drag elements to screens to add them
                        </p>
                    )}
                </div>

                {/* Element grid */}
                <div className="flex-1 p-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        {ELEMENT_TYPES.map((elementType) => {
                            const IconComponent = elementType.icon;
                            const isBeingDragged =
                                isDragFromPalette &&
                                draggedElementType === elementType.type;
                            return (
                                <div
                                    key={elementType.type}
                                    draggable
                                    onDragStart={(e) =>
                                        handleDragStart(e, elementType.type)
                                    }
                                    onDragEnd={handleDragEnd}
                                    className={`p-3 border rounded-lg cursor-grab hover:cursor-grabbing transition-colors ${
                                        isBeingDragged
                                            ? 'border-blue-500 bg-blue-100 shadow-md opacity-50'
                                            : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                    }`}
                                    title={`Drag to place ${elementType.description} on a screen grid position`}
                                >
                                    <div className="flex flex-col items-center text-center">
                                        <div
                                            className="p-2 rounded-lg mb-2"
                                            style={{
                                                backgroundColor: `${elementType.color}20`,
                                            }}
                                        >
                                            <IconComponent
                                                className="w-4 h-4"
                                                style={{
                                                    color: elementType.color,
                                                }}
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">
                                            {elementType.name}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Original floating mode
    return (
        <div
            className="absolute top-4 z-40 bg-white rounded-lg shadow-2xl border-2 border-blue-300 w-64"
            style={{ right: `${rightOffset}px` }}
        >
            <div className="p-4 border-b border-blue-200 bg-blue-50 flex items-center justify-between">
                <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">
                        Element Palette
                    </h3>
                    {isDragFromPalette && (
                        <p className="text-xs text-blue-600 mt-1">
                            Dragging:{' '}
                            {
                                ELEMENT_TYPES.find(
                                    (e) => e.type === draggedElementType,
                                )?.name
                            }
                            <br />
                            Drop on a screen grid position
                        </p>
                    )}
                </div>
                <div className="flex gap-1">
                    {isDragFromPalette && (
                        <button
                            onClick={handleCancelDrag}
                            className="text-red-400 hover:text-red-600 p-1"
                            title="Cancel placement"
                        >
                            <X size={16} />
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 p-1"
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="p-2 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                    {ELEMENT_TYPES.map((elementType) => {
                        const IconComponent = elementType.icon;
                        const isBeingDragged =
                            isDragFromPalette &&
                            draggedElementType === elementType.type;
                        return (
                            <div
                                key={elementType.type}
                                draggable
                                onDragStart={(e) =>
                                    handleDragStart(e, elementType.type)
                                }
                                onDragEnd={handleDragEnd}
                                className={`p-3 border rounded-lg cursor-grab hover:cursor-grabbing transition-colors ${
                                    isBeingDragged
                                        ? 'border-blue-500 bg-blue-100 shadow-md opacity-50'
                                        : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                }`}
                                title={`Drag to place ${elementType.description} on a screen grid position`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div
                                        className="p-2 rounded-lg mb-2"
                                        style={{
                                            backgroundColor: `${elementType.color}20`,
                                        }}
                                    >
                                        <IconComponent
                                            className="w-4 h-4"
                                            style={{ color: elementType.color }}
                                        />
                                    </div>
                                    <span className="text-xs font-medium text-gray-700">
                                        {elementType.name}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="p-3 border-t border-gray-200 text-xs text-gray-500">
                Drag elements to precise screen grid positions
            </div>
        </div>
    );
}
